import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Clock, Calendar, Users, Zap } from 'lucide-react';
import { courseInfo } from '../data/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const CountdownSection = ({ onEnrollClick }) => {
  // Rolling 7-day countdown
  const getTargetDate = () => {
    const target = new Date();
    target.setDate(target.getDate() + 7);
    target.setHours(23, 59, 59, 999);
    return target;
  };

  const [targetDate] = useState(getTargetDate);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.2 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const formatNumber = (num) => String(num).padStart(2, '0');

  return (
    <section className="countdown-section" data-testid="countdown-section">
      <div className="container">
        <div 
          ref={sectionRef}
          className={`countdown-wrapper animate-scale ${sectionVisible ? 'is-visible' : ''}`}
        >
          <div className="countdown-content">
            <div className={`countdown-badge animate-fade-in ${sectionVisible ? 'is-visible' : ''}`}>
              <Zap className="badge-icon" />
              Limited Time Offer
            </div>
            
            <h2 className={`countdown-title animate-slide-up ${sectionVisible ? 'is-visible' : ''} stagger-1`}>
              Secure Your Spot Now
            </h2>
            
            <p className={`countdown-subtitle animate-slide-up ${sectionVisible ? 'is-visible' : ''} stagger-2`}>
              Enrollment closes soon. Join thousands of students transforming their careers.
            </p>

            <div className={`countdown-timer animate-fade-in ${sectionVisible ? 'is-visible' : ''} stagger-3`}>
              <div className="time-block hover-scale">
                <span className="time-value">{formatNumber(timeLeft.days)}</span>
                <span className="time-label">Days</span>
              </div>
              <span className="time-separator">:</span>
              <div className="time-block hover-scale">
                <span className="time-value">{formatNumber(timeLeft.hours)}</span>
                <span className="time-label">Hours</span>
              </div>
              <span className="time-separator">:</span>
              <div className="time-block hover-scale">
                <span className="time-value">{formatNumber(timeLeft.minutes)}</span>
                <span className="time-label">Minutes</span>
              </div>
              <span className="time-separator">:</span>
              <div className="time-block hover-scale">
                <span className="time-value">{formatNumber(timeLeft.seconds)}</span>
                <span className="time-label">Seconds</span>
              </div>
            </div>

            <div className={`pricing-block animate-slide-up ${sectionVisible ? 'is-visible' : ''} stagger-4`}>
              <div className="price-info">
                <span className="current-price">{courseInfo.price}</span>
                <span className="original-price">{courseInfo.originalPrice}</span>
                <span className="discount-badge">50% OFF</span>
              </div>
            </div>

            <Button 
              className={`btn-primary countdown-cta hover-glow animate-slide-up ${sectionVisible ? 'is-visible' : ''} stagger-5`}
              onClick={onEnrollClick}
              data-testid="countdown-enroll-btn"
            >
              Enroll Now & Save 50%
            </Button>

            <div className={`countdown-features animate-fade-in ${sectionVisible ? 'is-visible' : ''} stagger-6`}>
              <div className="feature-item hover-scale">
                <Clock size={16} />
                <span>30-day money-back guarantee</span>
              </div>
              <div className="feature-item hover-scale">
                <Calendar size={16} />
                <span>Lifetime access</span>
              </div>
              <div className="feature-item hover-scale">
                <Users size={16} />
                <span>Community support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountdownSection;
